import { useState, useEffect, useMemo, type FormEvent } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import type { SchoolTypePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { PlacePicker } from "@/components/place-picker";
import type { SelectedPlace } from "@/components/place-picker";

// School edit form -- per checklist: name, abbreviation, location, latitude, longitude, school_type_id,
// logo_url, primary_color, secondary_color (085)

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function isHex(v: string): boolean {
  return v === "" || HEX_RE.test(v);
}

// WCAG relative-luminance contrast -- informational only; does not block saving
function contrastRatio(fg: string, bg: string): number | null {
  if (!HEX_RE.test(fg) || !HEX_RE.test(bg)) return null;
  const lum = (hex: string) => {
    const c = hex.slice(1).match(/.{2}/g)!.map((h) => parseInt(h, 16) / 255);
    const s = c.map((v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)));
    return 0.2126 * s[0] + 0.7152 * s[1] + 0.0722 * s[2];
  };
  const l1 = lum(fg);
  const l2 = lum(bg);
  const [a, b] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (a + 0.05) / (b + 0.05);
}

export default function SchoolEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [name, setName] = useState("");
  const [abbreviation, setAbbreviation] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [location, setLocation] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [googlePlaceId, setGooglePlaceId] = useState<string | null>(null);
  const [schoolTypeId, setSchoolTypeId] = useState("");
  const [schoolTypes, setSchoolTypes] = useState<SchoolTypePublic[]>([]);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [primaryColor, setPrimaryColor] = useState("");
  const [secondaryColor, setSecondaryColor] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoFetchUrl, setLogoFetchUrl] = useState("");
  const [logoFetching, setLogoFetching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    Promise.all([api.getSchool(id), api.getSchoolTypes()])
      .then(([schoolRes, typesRes]) => {
        setName(schoolRes.school.name);
        setAbbreviation(schoolRes.school.abbreviation ?? "");
        setWebsiteUrl(schoolRes.school.website_url ?? "");
        setLocation(schoolRes.school.location ?? "");
        setLatitude(schoolRes.school.latitude != null ? String(schoolRes.school.latitude) : "");
        setLongitude(schoolRes.school.longitude != null ? String(schoolRes.school.longitude) : "");
        setGooglePlaceId(schoolRes.school.google_place_id ?? null);
        setSchoolTypeId(schoolRes.school.school_type_id ?? "");
        setLogoUrl(schoolRes.school.logo_url);
        setPrimaryColor(schoolRes.school.primary_color ?? "");
        setSecondaryColor(schoolRes.school.secondary_color ?? "");
        setSchoolTypes(typesRes.school_types);
      })
      .catch((e) =>
        setError(e instanceof api.ApiError ? e.message : "Failed to load school"),
      )
      .finally(() => setLoading(false));
  }, [id]);

  const previewAbbr = abbreviation || name.slice(0, 3).toUpperCase() || "SCH";
  const contrast = useMemo(
    () => contrastRatio(secondaryColor, primaryColor),
    [primaryColor, secondaryColor],
  );

  async function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    setError(null);
    setLogoUploading(true);
    try {
      const res = await api.uploadSchoolLogo(id, file);
      setLogoUrl(res.school.logo_url);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to upload logo");
    } finally {
      setLogoUploading(false);
      e.target.value = "";
    }
  }

  async function handleLogoFetchFromUrl() {
    if (!id) return;
    const url = logoFetchUrl.trim();
    if (!url) return;
    setError(null);
    setLogoFetching(true);
    try {
      const res = await api.fetchSchoolLogoFromUrl(id, url);
      setLogoUrl(res.school.logo_url);
      setLogoFetchUrl("");
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to fetch logo");
    } finally {
      setLogoFetching(false);
    }
  }

  async function handleLogoDelete() {
    if (!id) return;
    setError(null);
    try {
      const res = await api.deleteSchoolLogo(id);
      setLogoUrl(res.school.logo_url);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to remove logo");
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    if (primaryColor && !isHex(primaryColor)) {
      setError("Primary colour must be a #RRGGBB hex value");
      return;
    }
    if (secondaryColor && !isHex(secondaryColor)) {
      setError("Secondary colour must be a #RRGGBB hex value");
      return;
    }
    setError(null);
    setSubmitting(true);

    try {
      await api.updateSchool(id, {
        school_type_id: schoolTypeId || null,
        name,
        abbreviation: abbreviation || null,
        website_url: websiteUrl || null,
        location: location || null,
        latitude: latitude ? parseFloat(latitude) : null,
        longitude: longitude ? parseFloat(longitude) : null,
        google_place_id: googlePlaceId || null,
        primary_color: primaryColor || null,
        secondary_color: secondaryColor || null,
      });
      navigate("/schools");
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to update school");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <p className="text-muted-foreground" data-page="school-edit" data-state="loading">Loading...</p>;
  }

  return (
    <div className="flex flex-col gap-6" data-page="school-edit" data-state={submitting ? "submitting" : error ? "error" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Edit School</h1>
        <p className="text-muted-foreground">Update school details</p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">School Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-region="form" data-form="school-edit">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            <div className="flex flex-col gap-2">
              <Label htmlFor="schoolType">School Type</Label>
              <select
                id="schoolType"
                data-field="school-type"
                value={schoolTypeId}
                onChange={(e) => setSchoolTypeId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">-- None --</option>
                {schoolTypes.map((st) => (
                  <option key={st.id} value={st.id}>
                    {st.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="name">School Name</Label>
              <Input
                id="name"
                data-field="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="abbreviation">Abbreviation</Label>
              <Input
                id="abbreviation"
                data-field="abbreviation"
                placeholder="e.g. h/s Menlopark"
                value={abbreviation}
                onChange={(e) => setAbbreviation(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="website_url">Website</Label>
              <Input
                id="website_url"
                data-field="website-url"
                type="url"
                placeholder="e.g. https://menlopark.co.za"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <PlacePicker
                label="Find on Google Places (fills location, coordinates & Place ID)"
                placeholder="Search for school name or address…"
                onSelect={(place: SelectedPlace) => {
                  setName(place.name);
                  setLocation(place.address);
                  setLatitude(String(place.lat));
                  setLongitude(String(place.lng));
                  setGooglePlaceId(place.id);
                }}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                data-field="location"
                placeholder="e.g. Pretoria, Gauteng"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="latitude">Latitude</Label>
                <Input
                  id="latitude"
                  data-field="latitude"
                  type="number"
                  step="any"
                  placeholder="e.g. -25.7646"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="longitude">Longitude</Label>
                <Input
                  id="longitude"
                  data-field="longitude"
                  type="number"
                  step="any"
                  placeholder="e.g. 28.2583"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                />
              </div>
            </div>

            <div className="border-t border-border pt-4" data-region="branding">
              <h2 className="mb-3 text-sm font-semibold text-foreground">Branding</h2>

              <div className="flex flex-col gap-2">
                <Label htmlFor="logo">Crest / Logo</Label>
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-md border border-border bg-muted"
                    data-region="logo-preview"
                  >
                    {logoUrl ? (
                      <img src={logoUrl} alt="School logo" className="h-full w-full object-contain" />
                    ) : (
                      <span className="text-xs text-muted-foreground">No logo</span>
                    )}
                  </div>
                  <label
                    className={buttonVariants({ variant: "outline", size: "sm" })}
                    data-action="logo-pick"
                    aria-disabled={logoUploading ? "true" : "false"}
                  >
                    {logoUploading ? "Uploading…" : logoUrl ? "Replace" : "Upload"}
                    <input
                      id="logo"
                      data-field="logo"
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      className="hidden"
                      onChange={handleLogoChange}
                      disabled={logoUploading}
                    />
                  </label>
                  {logoUrl && (
                    <Button type="button" variant="ghost" size="sm" onClick={handleLogoDelete} data-action="logo-delete">
                      Remove
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">PNG, JPEG, WebP or SVG, up to 5MB.</p>

                <div className="mt-3 flex items-end gap-2" data-region="logo-fetch-from-url">
                  <div className="flex-1 flex flex-col gap-1">
                    <Label htmlFor="logo_fetch_url" className="text-xs text-muted-foreground">Or paste a URL to your crest image</Label>
                    <Input
                      id="logo_fetch_url"
                      data-field="logo-fetch-url"
                      type="url"
                      placeholder="https://yourschool.co.za/about/crest.png"
                      value={logoFetchUrl}
                      onChange={(e) => setLogoFetchUrl(e.target.value)}
                      disabled={logoFetching}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleLogoFetchFromUrl}
                    disabled={logoFetching || !logoFetchUrl.trim()}
                    data-action="logo-fetch"
                  >
                    {logoFetching ? "Fetching…" : "Fetch"}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">We download and host the image -- same end result as upload.</p>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="primary_color">Primary colour</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={HEX_RE.test(primaryColor) ? primaryColor : "#000000"}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="h-10 w-10 cursor-pointer rounded-md border border-input bg-background"
                      data-field="primary-color-picker"
                      aria-label="Primary colour picker"
                    />
                    <Input
                      id="primary_color"
                      data-field="primary-color"
                      placeholder="#002F6C"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="secondary_color">Secondary colour</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={HEX_RE.test(secondaryColor) ? secondaryColor : "#FFFFFF"}
                      onChange={(e) => setSecondaryColor(e.target.value)}
                      className="h-10 w-10 cursor-pointer rounded-md border border-input bg-background"
                      data-field="secondary-color-picker"
                      aria-label="Secondary colour picker"
                    />
                    <Input
                      id="secondary_color"
                      data-field="secondary-color"
                      placeholder="#FFFFFF"
                      value={secondaryColor}
                      onChange={(e) => setSecondaryColor(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {(primaryColor || secondaryColor || logoUrl) && (
                <div className="mt-4 flex items-center gap-3" data-region="branding-preview">
                  <span className="text-xs text-muted-foreground">Preview</span>
                  <div
                    className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold"
                    style={{
                      backgroundColor: HEX_RE.test(primaryColor) ? primaryColor : "var(--muted)",
                      color: HEX_RE.test(secondaryColor) ? secondaryColor : "var(--foreground)",
                    }}
                  >
                    {logoUrl && <img src={logoUrl} alt="" className="h-5 w-5 object-contain" />}
                    <span>{previewAbbr}</span>
                  </div>
                  {contrast !== null && (
                    <span
                      className={
                        contrast < 4.5
                          ? "text-xs text-amber-600"
                          : "text-xs text-muted-foreground"
                      }
                      data-region="contrast-hint"
                    >
                      Contrast {contrast.toFixed(1)}:1
                      {contrast < 4.5 ? " (low -- text may be hard to read)" : " (good)"}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" data-action="submit" disabled={submitting}>
                {submitting ? "Saving..." : "Save Changes"}
              </Button>
              <Link to={`/schools/${id}`} className={buttonVariants({ variant: "outline" })} data-action="cancel">
                Cancel
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
